import { useEffect, useState, useCallback } from "react"
import styled from "styled-components"
import { toast } from "react-hot-toast"
import { Plus, X, ListChecks, ChevronRight, Trash, Pin, PinOff } from "lucide-react"
import { confirmDialog } from "../../utils/confirmDialog"
import { useSocket } from "../../context/SocketContext"

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 900px) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
`

const Modal = styled.div`
  background: white;
  width: 100%;
  max-width: 520px;
  max-height: 80vh;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }
`

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  &:hover { background: #f1f5f9; color: #0f172a; }
`

const InputRow = styled.form`
  display: flex;
  gap: 8px;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
`

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  &:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 1px #2563eb; }
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  &:hover:not(:disabled) { background: #1d4ed8; }
  &:disabled { background: #cbd5e1; cursor: not-allowed; }
`

const ListBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 16px;
`

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 8px;
  border-radius: 8px;
  transition: background 0.1s;
  &:hover { background: #f9fafb; }
  & + & { border-top: 1px solid #f3f4f6; }
`

const ItemText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #0f172a;
  word-break: break-word;
`

const PickBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  &:hover { background: #059669; }
`

const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  flex-shrink: 0;
  &:hover { background: #fee2e2; color: #dc2626; }
`

const Empty = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
`

const Footer = styled.div`
  padding: 10px 20px;
  border-top: 1px solid #f1f5f9;
  background: #f9fafb;
  font-size: 12px;
  color: #64748b;
`

export default function BacklogModal({ isOpen, onClose, chartId, onPickToTask }) {
  const { socket, currentChartId } = useSocket()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [newTitle, setNewTitle] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    if (!chartId) return
    setLoading(true)
    try {
      const res = await fetch(`/request/backlog?chart_id=${chartId}`, { credentials: "include" })
      const data = await res.json()
      if (data.success) setItems(data.items || [])
      else toast.error(data.message || "백로그 조회 실패")
    } catch (e) {
      console.error(e)
      toast.error("서버 연결 오류")
    } finally {
      setLoading(false)
    }
  }, [chartId])

  useEffect(() => {
    if (isOpen) load()
  }, [isOpen, load])

  // 실시간 동기화: 같은 차트의 다른 멤버가 백로그 변경 시 자동 반영
  useEffect(() => {
    if (!isOpen || !socket || !chartId || String(chartId) !== String(currentChartId)) return

    const onAdded = ({ item, userId }) => {
      if (!item) return
      setItems((prev) => {
        if (prev.some((i) => i.id === item.id)) return prev
        return [...prev, item].sort((a, b) =>
          b.is_pinned === a.is_pinned ? a.sort_order - b.sort_order : (b.is_pinned ? 1 : -1)
        )
      })
    }
    const onUpdated = ({ item }) => {
      if (!item) return
      setItems((prev) =>
        prev
          .map((i) => (i.id === item.id ? { ...i, ...item } : i))
          .sort((a, b) =>
            b.is_pinned === a.is_pinned ? a.sort_order - b.sort_order : (b.is_pinned ? 1 : -1)
          )
      )
    }
    const onDeleted = ({ id }) => {
      if (id == null) return
      setItems((prev) => prev.filter((i) => i.id !== id))
    }

    socket.on("backlog:added", onAdded)
    socket.on("backlog:updated", onUpdated)
    socket.on("backlog:deleted", onDeleted)
    return () => {
      socket.off("backlog:added", onAdded)
      socket.off("backlog:updated", onUpdated)
      socket.off("backlog:deleted", onDeleted)
    }
  }, [isOpen, socket, chartId, currentChartId])

  const handleAdd = async (e) => {
    e?.preventDefault()
    const title = newTitle.trim()
    if (!title) return
    setSubmitting(true)
    try {
      const res = await fetch("/request/backlog", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ chart_id: chartId, title }),
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.message)
      setItems((prev) => [...prev, data.item])
      setNewTitle("")
    } catch (err) {
      console.error(err)
      toast.error(err.message || "추가 실패")
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (item) => {
    if (!(await confirmDialog(`"${item.title}" 항목을 삭제할까요?`))) return
    try {
      const res = await fetch(`/request/backlog/${item.id}`, {
        method: "DELETE",
        credentials: "include",
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.message)
      setItems((prev) => prev.filter((i) => i.id !== item.id))
    } catch (err) {
      console.error(err)
      toast.error(err.message || "삭제 실패")
    }
  }

  const handlePick = async (item) => {
    // 부모(page.jsx)에서 실제 task 추가 처리
    onPickToTask?.(item)
    // 고정(핀) 항목은 다시 쓸 수 있도록 백로그에 그대로 둠
    if (item.is_pinned) return
    // 일반 항목은 백로그에서 자동 제거
    try {
      await fetch(`/request/backlog/${item.id}`, {
        method: "DELETE",
        credentials: "include",
      })
      setItems((prev) => prev.filter((i) => i.id !== item.id))
    } catch (err) {
      console.error("백로그 자동 제거 실패:", err)
    }
  }

  const handleTogglePin = async (item) => {
    const next = !item.is_pinned
    // 낙관적 업데이트
    setItems((prev) =>
      prev
        .map((i) => (i.id === item.id ? { ...i, is_pinned: next } : i))
        // 핀 우선 정렬 유지
        .sort((a, b) => (b.is_pinned === a.is_pinned ? a.sort_order - b.sort_order : (b.is_pinned ? 1 : -1)))
    )
    try {
      const r = await fetch(`/request/backlog/${item.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_pinned: next }),
      })
      const data = await r.json()
      if (!data.success) throw new Error(data.message)
    } catch (err) {
      // 실패 시 롤백
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_pinned: !next } : i)))
      toast.error(err.message || "핀 토글 실패")
    }
  }

  if (!isOpen) return null

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <Modal>
        <Header>
          <Title>
            <ListChecks size={18} color="#2563eb" /> 작업 목록 (백로그)
          </Title>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        <InputRow onSubmit={handleAdd}>
          <Input
            placeholder="할 일을 입력하고 Enter 또는 추가"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            autoFocus
            disabled={!chartId}
          />
          <AddBtn type="submit" disabled={!chartId || !newTitle.trim() || submitting}>
            <Plus size={14} /> 추가
          </AddBtn>
        </InputRow>

        <ListBody>
          {!chartId ? (
            <Empty>차트를 먼저 저장하거나 불러와주세요</Empty>
          ) : loading ? (
            <Empty>불러오는 중…</Empty>
          ) : items.length === 0 ? (
            <Empty>등록된 할 일이 없어요. 위에서 추가해보세요.</Empty>
          ) : (
            items.map((item) => (
              <Item key={item.id} style={item.is_pinned ? { background: "#fef9c3", borderRadius: 8 } : undefined}>
                <ItemText>
                  {item.is_pinned && <Pin size={12} style={{ marginRight: 4, color: "#ca8a04", verticalAlign: "middle" }} />}
                  {item.title}
                </ItemText>
                <PickBtn
                  onClick={() => handlePick(item)}
                  title={item.is_pinned
                    ? "고정 항목 — 작업으로 추가해도 목록에 그대로 남음 (반복 사용)"
                    : "이 항목을 작업으로 추가 (오늘부터 7일)"}
                >
                  작업으로 <ChevronRight size={13} />
                </PickBtn>
                <RemoveBtn
                  onClick={() => handleTogglePin(item)}
                  title={item.is_pinned ? "고정 해제 — 다음 작업으로 추가 시 자동 제거됨" : "고정 — 작업으로 추가해도 목록에서 안 사라짐"}
                  style={item.is_pinned ? { color: "#ca8a04" } : undefined}
                >
                  {item.is_pinned ? <PinOff size={14} /> : <Pin size={14} />}
                </RemoveBtn>
                <RemoveBtn onClick={() => handleRemove(item)} title="항목 삭제">
                  <Trash size={14} />
                </RemoveBtn>
              </Item>
            ))
          )}
        </ListBody>

        <Footer>
          "작업으로" 클릭 시 오늘부터 7일짜리 작업으로 등록 · 목록에서 제거.
          📌 핀 표시한 항목은 반복 사용용으로 목록에 남아있어요.
        </Footer>
      </Modal>
    </Overlay>
  )
}
